<template>
  <div>
    <div class="is-flex is-justify-content-space-between is-align-items-center">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item active>
          {{ $t('projects.title') }}
        </b-breadcrumb-item>
      </b-breadcrumb>
      <b-button type="is-primary" @click="$router.push({ name: 'project-gallery' })">{{ $t('projects.addNew') }}</b-button>
    </div>
    <div class="list-table-container">
      <b-table :data="rows" striped :loading="loading" @click="handleRowClick"
        sort-icon="menu-up" default-sort="name" default-sort-direction="asc">
        <b-table-column field="name" sortable :label="$t('projects.name')" v-slot="props">
          {{ props.row.name }}
        </b-table-column>
        <b-table-column field="updatedAt" sortable :label="$t('projects.lastUpdated')" v-slot="props">
          {{ $d(new Date(props.row.updatedAt), 'short') }}
        </b-table-column>
        <b-table-column field="firmwareSha" sortable :label="$t('projects.latestFirmware')" v-slot="props">
          <span v-if="props.row.firmwareSha">
            <!-- <a href="" @click.stop.prevent="goToFirmware(String(props.row.firmwareSha))"> -->
              <router-link
              :to="{ name: 'firmware-details', params: { sha: props.row.firmwareSha } }"
              @click.stop
              class="is-family-monospace"
            >
              {{ shortSha(props.row.firmwareSha) }}
            </router-link>
            <!-- </a> -->
          </span>
          <span v-else>—</span>
        </b-table-column>
        <b-table-column field="lastBuildAt" sortable :label="$t('projects.lastSuccessfulBuild')" v-slot="props">
          {{ props.row.lastBuildAt ? $d(new Date(props.row.lastBuildAt), 'short') : '—' }}
        </b-table-column>
        <b-table-column :label="$t('global.action')" v-slot="props">
          <div class="control is-flex is-align-items-center">
            <b-button type="is-danger" size="is-small" icon-left="delete" :title="$t('global.delete')" aria-label="delete" @click.stop="confirmRemove(props.row)" />
          </div>
        </b-table-column>
        <template #empty>
          <div class="has-text-centered mt-4 mb-4">{{ $t('projects.noneYet') }}</div>
        </template>
      </b-table>
    </div>
  </div>
  
</template>

<script lang="ts">
import { defineComponent, computed, getCurrentInstance, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

type ProjectRow = { id: string; name: string; updatedAt?: string | number | Date; firmwareSha?: string; lastBuildAt?: string | number | Date }

export default defineComponent({
  name: 'ProjectsList',
  setup() {
    const router = useRouter()
    const store = useStore()
    const rows = computed<ProjectRow[]>(() => Object.values(store.state.projects || {}) as ProjectRow[])
    const loading = computed<boolean>(() => store.state.loading.projects)
    const vm = getCurrentInstance()

    const load = async (force = false) => {
      await store.dispatch('loadModel', { model: 'projects', force })
    }

    const remove = async (row: ProjectRow) => {
      try {
        await store.dispatch('deleteProject', { id: row.id })
        await store.dispatch('loadModel', { model: 'projects', force: true })
      } catch (e) {
        // ignore
      }
    }

    const confirmRemove = (row: ProjectRow) => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      type I18nProxy = { $t?: (key: string, vars?: Record<string, string>) => string }
      const proxy = vm?.proxy as unknown as DialogProxy & I18nProxy
      const msg = proxy?.$t ? proxy.$t('projects.confirmDelete', { name: row.name }) : `Delete project ${row.name}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: () => { remove(row) }
      })
    }

    const handleRowClick = (row: ProjectRow) => {
      router.push({ name: 'project-edit', params: { id: row.id } })
    }

    const goToFirmware = (sha: string) => {
      router.push({ name: 'firmware-details', params: { sha } })
    }

    const shortSha = (sha?: string) => {
      if (!sha) return ''
      const s = String(sha)
      if (s.length <= 16) return s
      return `${s.slice(0, 8)}…${s.slice(-6)}`
    }

    onMounted(load)
    return { rows, loading, handleRowClick, confirmRemove, shortSha, goToFirmware }
  }
})
</script>

<style scoped>
</style>


